/**
 * LaunchBits — co-located pieces for the Launch step (7/7).
 *
 * The celebratory success moment: a mascot disc crowned with a brand-success
 * check + a soft confetti burst, the hero share-link card (gradient border,
 * Copy + Open), and the three mini channel cards (Phone, QR, Activation pack).
 *
 * Brand law: genie-* tokens + the signature gradient only; "Help Genie" two
 * words; no "AI" language; no em dashes; Lucide for inline glyphs, GenieIcon
 * for the channel chips.
 */
import { useState } from "react";
import { Check, Copy, ExternalLink, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { VoiceMascot } from "@/components/voice-genie/VoiceMascot";
import type { VoiceMascotKey } from "@/types/voice-vision";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";

/* ------------------------------------------------------------------ */
/* success-badge — mascot disc + green-success check + confetti burst  */
/* ------------------------------------------------------------------ */

const CONFETTI = [
  { left: "8%", top: "12%", color: "#4E9CFF", size: 7, delay: "0ms" },
  { left: "88%", top: "18%", color: "#FF4DB8", size: 9, delay: "90ms" },
  { left: "18%", top: "70%", color: "#FF4DB8", size: 6, delay: "150ms" },
  { left: "80%", top: "66%", color: "#4E9CFF", size: 8, delay: "60ms" },
  { left: "50%", top: "2%", color: "#FFBB33", size: 6, delay: "200ms" },
  { left: "2%", top: "44%", color: "#FFBB33", size: 5, delay: "120ms" },
  { left: "96%", top: "44%", color: "#4E9CFF", size: 6, delay: "240ms" },
];

export const SuccessBadge = ({ mascot }: { mascot: VoiceMascotKey }) => {
  return (
    <div
      data-hg-block="success-badge"
      className="relative flex items-center justify-center"
      style={{ width: 156, height: 156 }}
    >
      {/* confetti burst */}
      {CONFETTI.map((c, i) => (
        <span
          key={i}
          aria-hidden
          className="absolute rounded-full opacity-90 animate-in fade-in zoom-in"
          style={{
            left: c.left,
            top: c.top,
            width: c.size,
            height: c.size,
            backgroundColor: c.color,
            animationDelay: c.delay,
            animationDuration: "600ms",
          }}
        />
      ))}

      {/* soft ring + mascot */}
      <div className="relative">
        <VoiceMascot mascot={mascot} size={96} rounded discSize={120} />
        {/* success check */}
        <div className="absolute -bottom-1 -right-1 w-10 h-10 rounded-full bg-genie-success border-[3px] border-white flex items-center justify-center shadow-md">
          <Check className="w-5 h-5 text-white" strokeWidth={3} />
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* share-link — gradient-border hero with the genie's public page URL  */
/* ------------------------------------------------------------------ */

export const ShareLinkHero = ({ url }: { url: string }) => {
  const [copied, setCopied] = useState(false);
  const display = url.replace(/^https?:\/\//, "");

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied", { description: "Share it anywhere your customers are." });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy the link. Long-press to copy it manually.");
    }
  };

  return (
    <div
      data-hg-block="share-link"
      className="w-full rounded-2xl p-[1.5px] bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-[0_14px_40px_-18px_rgba(78,156,255,0.5)]"
    >
      <div className="rounded-[15px] bg-white p-3.5 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] flex items-center justify-center flex-shrink-0">
          <GenieIcon name="share" size={18} color="#fff" label="Share" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.6px] text-gray-400">
            Your genie's page
          </p>
          <p className="text-[15px] font-bold text-[#222B45] truncate">{display}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="h-9 px-3.5 rounded-xl border border-gray-200 text-[13px] font-semibold text-[#64748B] hover:bg-genie-background transition-colors hidden sm:inline-flex items-center gap-1.5"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Open
          </a>
          <button
            type="button"
            onClick={handleCopy}
            className="h-9 px-4 rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] text-[13px] font-bold text-white shadow-[0_8px_20px_-10px_rgba(78,156,255,0.55)] hover:opacity-90 transition-all inline-flex items-center gap-1.5"
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? "Copied" : "Copy link"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* mini-channels — three compact channel teasers                       */
/* ------------------------------------------------------------------ */

interface MiniChannel {
  genieIcon: GenieIconName;
  tint: string;
  label: string;
}

// Brand tokens only: secondary (pink), accent (amber), primary (blue).
// Wireframe's purple QR maps to genie-accent per the token map (purple is banned).
const CHANNELS: MiniChannel[] = [
  { genieIcon: "phone", tint: "#FF4DB8", label: "Phone number" },
  { genieIcon: "qr-code", tint: "#FFBB33", label: "QR code" },
  { genieIcon: "genie-lamp", tint: "#4E9CFF", label: "Activation pack" },
];

export const MiniChannels = () => {
  return (
    <div className="w-full">
      <div data-hg-block="mini-channels" className="w-full flex gap-3">
        {CHANNELS.map((c) => (
          <div
            key={c.label}
            className="flex-1 min-w-0 rounded-2xl border border-gray-200 bg-white p-4 flex flex-col items-center gap-2"
          >
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${c.tint}1a` }}
            >
              <GenieIcon name={c.genieIcon} size={19} color={c.tint} label={c.label} />
            </div>
            <span className="text-[12.5px] font-semibold text-[#222B45] text-center leading-tight">
              {c.label}
            </span>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-center gap-1.5 mt-4">
        <span className="text-[12.5px] font-semibold text-gray-500">
          Website embed, email signature and more inside
        </span>
        <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
      </div>
    </div>
  );
};
